import { StarRating } from "@/components/starRating/starRating.component";
import { Text } from "@/components/text/text.component";

import type { AdtCustomerReviewsSection as SanityAdtCustomerReviewsSection } from "@/sanity.types";

import { AdtCustomerReviewQuote } from "./adtCustomerReviewQuote.component";

export function AdtCustomerReviewsSection({
  block,
}: AdtCustomerReviewsSectionProps) {
  const reviews = (block.reviews ?? []).filter(isRenderableReview);

  if (!block.title || reviews.length === 0) return null;

  const titleId = `customer-reviews-title-${block._key}`;

  const renderReviews = () => {
    return reviews.map((review) => {
      const rating = review.rating ?? 5;
      return (
        <li
          key={review._key}
          className="flex flex-col items-start"
          data-testid="customer-review">
          <Text variant="h5" component="h3" className="text-greyscale-headline">
            {review.name}
          </Text>
          <Text
            variant="body1"
            component="p"
            className="text-greyscale-body mt-1">
            {review.location}
          </Text>
          <div
            className="mt-3 flex items-center gap-0.5"
            role="img"
            aria-label={`${rating} out of 5 stars`}>
            <StarRating
              rating={rating}
              width={18}
              height={18}
              className="fill-adt-blue"
            />
          </div>
          <div className="mt-4 w-full">
            <AdtCustomerReviewQuote quote={review.quote} />
          </div>
        </li>
      );
    });
  };

  return (
    <section
      aria-labelledby={titleId}
      className="w-full bg-greyscale-background py-10"
      data-testid="customer-reviews-section">
      <div className="container mx-auto flex flex-col gap-10 px-5 md:px-8">
        <Text
          variant="h3"
          component="h2"
          id={titleId}
          className="text-greyscale-headline text-center">
          {block.title}
        </Text>
        <ul
          className="grid grid-cols-1 gap-10 md:grid-cols-3 md:gap-8 lg:gap-12"
          data-testid="customer-reviews-list">
          {renderReviews()}
        </ul>
      </div>
    </section>
  );
}

function isRenderableReview(
  review: AdtCustomerReview | null | undefined,
): review is AdtCustomerReview {
  return Boolean(review?._key && review.name && review.quote);
}

export type AdtCustomerReviewsSectionBlock = {
  _key: string;
} & SanityAdtCustomerReviewsSection;

type AdtCustomerReview = NonNullable<
  AdtCustomerReviewsSectionBlock["reviews"]
>[number];

type AdtCustomerReviewsSectionProps = {
  block: AdtCustomerReviewsSectionBlock;
};
